import {ContentBox} from '@hconnect/common/components/ContentBox'
import {useNavigationBlocker} from '@hconnect/common/hooks'
import {
  DisabledFields,
  HiddenFields,
  DowntimeStoppageCodes,
  StoppageDto as Stoppage,
  StoppageKind
} from '@hconnect/common/types'
import {validateAll} from '@hconnect/common/validators'
import {FeatureFlagKey, useCommonFeatureFlag} from '@hproduce/feature-flags'
import {Check, Close} from '@mui/icons-material'
import {Box, Button} from '@mui/material'
import {isEqual} from 'lodash'
import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {StoppageForm, ConfirmCloseDialog, EventTypeTag} from '../components'
import {COMMON_SHIFT_EVENT_WIDGET_NAMESPACE} from '../consts'
import {useTypeSpecificValidatorConfig} from '../hooks'

export type EventFormContainerProps = {
  item: Partial<Stoppage>
  doClose(): void
  doSubmit(item: Stoppage): void
  plantId: string
  timezone: string
  stoppageCodes: DowntimeStoppageCodes
  isCreateMode?: boolean
  children?: React.ReactNode
  renderFormHeaderActions?: (props: {doClose: () => void}) => React.ReactNode
  renderFormFooterActions?: (props: {
    doSubmit: () => void
    doClose: () => void
    isDirty: boolean
    isSubmitDisabled: boolean
  }) => React.ReactNode
  bodyWithPadding?: boolean
  isAutocompleteProcessStage: boolean
  eventTypeLabelPrefix?: string
  disabledFields?: DisabledFields
  hiddenFields?: HiddenFields
  isEquipmentAvailable?: boolean
  skipOverlapValidation?: boolean
}

type State = {
  originalItem: Partial<Stoppage>
  item: Partial<Stoppage>
  showValidation: boolean
  validationError: Map<keyof Stoppage, string>
}

const SUPPORTED_AUTOCOMPLETE_PROCESS_STAGE_PLANTS = ['CI4C']
// helper to maintain a fix pointer for an empty validationError map
const emptyMap = new Map()

const PLANTS_WITH_EXTENDED_EQUIPMENT_IDS = ['CI4C']

export const StoppageFormContainer: React.FC<EventFormContainerProps> = ({
  item: originalItem,
  doClose,
  doSubmit: parentDoSubmit,
  children,
  plantId,
  renderFormHeaderActions,
  renderFormFooterActions,
  bodyWithPadding = true,
  eventTypeLabelPrefix,
  disabledFields,
  isAutocompleteProcessStage,
  hiddenFields,
  timezone,
  stoppageCodes,
  isEquipmentAvailable,
  skipOverlapValidation
}) => {
  // state setup
  const [state, setState] = useState<State>({
    originalItem,
    item: originalItem,
    showValidation: false,
    validationError: emptyMap
  })

  const isFunctionalLocationOptional = useCommonFeatureFlag(
    FeatureFlagKey.FunctionalLocationOptional,
    {
      plantId
    }
  )
  const isOverlapValidationEnabled = useCommonFeatureFlag(FeatureFlagKey.StoppageOverlapValidation)

  useEffect(() => {
    setState((currentState: State) => ({
      ...currentState,
      originalItem,
      item: originalItem
    }))
  }, [originalItem])

  // variables
  const item = state.item
  const disableSubmit = state.showValidation && state.validationError.size > 0
  const isDirty = JSON.stringify(state.originalItem) !== JSON.stringify(state.item)

  // hooks
  const {t} = useTranslation(COMMON_SHIFT_EVENT_WIDGET_NAMESPACE)
  const {setIsBlockActive, isBlocked, handleClose} = useNavigationBlocker(
    !isEqual(item, originalItem)
  )
  const enableAutocompleteProcessStage =
    isAutocompleteProcessStage && SUPPORTED_AUTOCOMPLETE_PROCESS_STAGE_PLANTS.includes(plantId)

  const config = useTypeSpecificValidatorConfig(
    plantId,
    item,
    skipOverlapValidation || !isOverlapValidationEnabled
  )

  const isFunctionalLocationRequired =
    isEquipmentAvailable &&
    !isFunctionalLocationOptional &&
    item.eventType !== StoppageKind.PlannedStoppage

  const memoizedHiddenFields = useMemo(() => hiddenFields, [hiddenFields])
  const activeValidatorConfig = useMemo(() => {
    if (memoizedHiddenFields) {
      const hiddenFieldsKeys = memoizedHiddenFields ? Object.keys(memoizedHiddenFields) : []

      if (hiddenFieldsKeys.includes('equipment') && hiddenFieldsKeys.includes('mainEquipment')) {
        config.delete('mainEquipmentNumbers')
      }
      hiddenFieldsKeys.forEach((key) => {
        if (memoizedHiddenFields[key] && key !== 'stoppageCode') {
          config.delete(key)
        }
      })
    }
    if (!isFunctionalLocationRequired) {
      config.delete('equipmentNumber')
    }
    return config
  }, [config, memoizedHiddenFields, isFunctionalLocationRequired])

  useEffect(() => {
    setState((currentState: State) => {
      if (!currentState.showValidation) return currentState
      const validationError = validateAll<Stoppage>(activeValidatorConfig, currentState.item)
      return {
        ...currentState,
        validationError
      }
    })
  }, [activeValidatorConfig])

  // callbacks
  const doSubmit = () => {
    const validationError = validateAll<Stoppage>(activeValidatorConfig, item)

    if (validationError.size === 0) {
      parentDoSubmit(item as Stoppage)
    } else {
      setState((currentState: State) => ({
        ...currentState,
        showValidation: true,
        validationError
      }))
    }
  }

  const updateMultiPart = useCallback(
    (delta: Partial<Stoppage>) => {
      setState((currentState: State) => {
        const nextItem = {
          ...currentState.item,
          ...delta
        } as Stoppage

        const validationError = currentState.showValidation
          ? validateAll<Stoppage>(activeValidatorConfig, nextItem) // TODO instead of validate all, just validate the "keyof delta"
          : currentState.validationError

        return {
          ...currentState,
          item: nextItem,
          validationError
        }
      })
    },
    [activeValidatorConfig]
  )

  const equipmentIdType = PLANTS_WITH_EXTENDED_EQUIPMENT_IDS.includes(plantId)
    ? 'sapNumber'
    : undefined

  return (
    <ContentBox
      data-test-id="event-form"
      bodyWithPadding={bodyWithPadding}
      title={
        item.eventType && (
          <EventTypeTag eventType={item.eventType} eventTypeLabelPrefix={eventTypeLabelPrefix} />
        )
      }
      afterTitle={renderFormHeaderActions ? renderFormHeaderActions({doClose}) : undefined}
      mode="max100PercentOfParentHeight"
    >
      <StoppageForm
        plantId={plantId}
        stoppageCodes={stoppageCodes}
        hiddenFields={hiddenFields}
        timezone={timezone}
        item={item}
        updateMultiPart={updateMultiPart}
        validationError={state.showValidation ? state.validationError : emptyMap}
        disabledFields={disabledFields}
        autocompleteProcessStage={enableAutocompleteProcessStage}
        equipmentIdType={equipmentIdType}
        isFunctionalLocationRequired={isFunctionalLocationRequired}
      >
        {children}
      </StoppageForm>
      {renderFormFooterActions ? (
        renderFormFooterActions({doSubmit, doClose, isDirty, isSubmitDisabled: disableSubmit})
      ) : (
        <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
          <Button
            startIcon={<Close />}
            variant="text"
            onClick={doClose}
            data-test-id="event-form-cancel-button"
          >
            {t('shiftEvent.action.cancel')}
          </Button>
          <Button
            startIcon={<Check />}
            disabled={disableSubmit}
            color="primary"
            variant="contained"
            onClick={doSubmit}
            data-test-id="event-save-button"
          >
            {t('shiftEvent.action.save')}
          </Button>
        </Box>
      )}
      <ConfirmCloseDialog
        open={isBlocked}
        onCancel={handleClose}
        onConfirm={() => {
          handleClose()
          setIsBlockActive(false)
        }}
      />
    </ContentBox>
  )
}
